/**
 * Google Analytics Event Tracker
 *
 * HTML 요소에 data-ga-* 속성을 추가하여 자동으로 이벤트를 전송합니다.
 *
 * 사용법:
 *
 * 1. 클릭 이벤트:
 *    <button data-ga-event="click" data-ga-category="button" data-ga-action="submit" data-ga-label="form_submit">
 *      제출
 *    </button>
 *
 * 2. 페이지뷰 트래킹:
 *    <div data-ga-event="view" data-ga-category="section" data-ga-action="view" data-ga-label="hero_section">
 *
 * 3. 링크 클릭:
 *    <a href="/contact" data-ga-event="click" data-ga-category="navigation" data-ga-action="click" data-ga-label="contact_link">
 *
 * 4. 폼 제출:
 *    <form data-ga-event="submit" data-ga-category="form" data-ga-action="submit" data-ga-label="inquiry_form">
 *
 * 5. 비디오 재생:
 *    <video data-ga-event="play" data-ga-category="video" data-ga-action="play" data-ga-label="intro_video">
 *
 * 6. 스크롤 트래킹 (50%, 75%, 100%):
 *    <section data-ga-event="scroll" data-ga-category="engagement" data-ga-action="scroll" data-ga-label="content_section">
 *
 * 속성 설명:
 * - data-ga-event: 이벤트 타입 (click, view, submit, play, pause, scroll 등)
 * - data-ga-category: 이벤트 카테고리 (예: button, navigation, form, video)
 * - data-ga-action: 이벤트 액션 (예: click, submit, play, scroll)
 * - data-ga-label: 이벤트 라벨 (예: contact_button, hero_section)
 * - data-ga-value: 이벤트 값 (선택사항, 숫자)
 */

(function () {
  'use strict';

  // GA가 로드되었는지 확인
  function isGAReady() {
    return typeof window.gtag === 'function';
  }

  // GA 이벤트 전송
  function sendGAEvent(category, action, label, value, linkData) {
    if (!isGAReady()) {
      console.warn('[GA Tracker] gtag is not available');
      return;
    }

    const eventParams = {
      event_category: category,
      event_label: label,
    };

    if (value !== undefined && value !== null) {
      eventParams.value = parseInt(value, 10);
    }

    //홈페이지 메인 배너 클릭 시 구분, URL
    if (linkData !== undefined && linkData !== null) {
        //홈페이지 메인 배너 클릭 시 구분
        if (linkData.linkId !== undefined && linkData.linkId !== null) {
            eventParams.link_id = linkData.linkId;
        }
        //홈페이지 메인 배너 클릭 시 URL
        if (linkData.linkUrl !== undefined && linkData.linkUrl !== null) {
            eventParams.link_url = linkData.linkUrl;
        }
    }

    // 현재 페이지 정보 추가
    if (typeof pageUri !== 'undefined') {
      eventParams.page_uri = pageUri;
    }
    if (typeof lang !== 'undefined') {
      eventParams.language = lang;
    }

    window.gtag('event', action, eventParams);
  }

  // 클릭 이벤트 처리
  function handleClickEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category');
    const action = element.getAttribute('data-ga-action');
    const label = element.getAttribute('data-ga-label');
    const value = element.getAttribute('data-ga-value');

    element.addEventListener('click', function () {
      sendGAEvent(category, action, label, value);
    });
  }

  // 폼 제출 이벤트 처리
  function handleSubmitEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category');
    const action = element.getAttribute('data-ga-action');
    const label = element.getAttribute('data-ga-label');
    const value = element.getAttribute('data-ga-value');

    element.addEventListener('submit', function () {
      sendGAEvent(category, action, label, value);
    });
  }

  // 뷰 이벤트 처리 (Intersection Observer 사용)
  function handleViewEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category');
    const action = element.getAttribute('data-ga-action');
    const label = element.getAttribute('data-ga-label');
    const value = element.getAttribute('data-ga-value');
    const threshold = parseFloat(element.getAttribute('data-ga-threshold') || '0.5');

    let tracked = false;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !tracked) {
            tracked = true;
            sendGAEvent(category, action, label, value);
          }
        });
      },
      { threshold: threshold }
    );

    observer.observe(element);
  }

  // 비디오 재생 이벤트 처리
  function handleVideoEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category');
    const action = element.getAttribute('data-ga-action');
    const label = element.getAttribute('data-ga-label');

    if (action === 'play' || !action) {
      element.addEventListener('play', function () {
        sendGAEvent(category, 'play', label);
      });
    }

    if (action === 'pause') {
      element.addEventListener('pause', function () {
        sendGAEvent(category, 'pause', label);
      });
    }

    if (action === 'ended') {
      element.addEventListener('ended', function () {
        sendGAEvent(category, 'ended', label);
      });
    }

    // 진행률 트래킹 (25%, 50%, 75%, 100%)
    const milestones = [0.25, 0.5, 0.75, 1.0];
    const tracked = {};

    element.addEventListener('timeupdate', function () {
      const progress = element.currentTime / element.duration;

      milestones.forEach(function (milestone) {
        if (progress >= milestone && !tracked[milestone]) {
          tracked[milestone] = true;
          sendGAEvent(category, 'video_progress', label, Math.round(milestone * 100));
        }
      });
    });
  }

  // 스크롤 이벤트 처리
  function handleScrollEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category');
    const action = element.getAttribute('data-ga-action');
    const label = element.getAttribute('data-ga-label');

    const milestones = [0.5, 0.75, 1.0]; // 50%, 75%, 100%
    const tracked = {};

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          const elementRect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const elementHeight = element.offsetHeight;
          const scrolled = Math.min(Math.max(0, viewportHeight - elementRect.top) / elementHeight, 1);

          milestones.forEach(function (milestone) {
            if (scrolled >= milestone && !tracked[milestone]) {
              tracked[milestone] = true;
              sendGAEvent(category, action, label, Math.round(milestone * 100));
            }
          });
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1.0] }
    );

    observer.observe(element);
  }

  // 다운로드 링크 트래킹
  function handleDownloadEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category') || 'download';
    const action = element.getAttribute('data-ga-action') || 'click';
    const label = element.getAttribute('data-ga-label') || element.getAttribute('href');

    element.addEventListener('click', function () {
      sendGAEvent(category, action, label);
    });
  }

  // 외부 링크 트래킹
  function handleOutboundEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category') || 'outbound';
    const action = element.getAttribute('data-ga-action') || 'click';
    const label = element.getAttribute('data-ga-label') || element.getAttribute('href');
    //메인 배너 클릭 시 구분값 link id로 요청
    const linkId = element.getAttribute('data-ga-link-id');
    const linkUrl = element.getAttribute('href');

    const linkData = {
        linkId:linkId,
        linkUrl:linkUrl
    }

    element.addEventListener('click', function () {
      sendGAEvent(category, action, label, null, linkData);
    });
  }

  // 스와이퍼 슬라이드 트래킹
  function handleSwiperEvent(element) {
    // 이미 초기화된 요소는 건너뛰기
    if (element.dataset.gaInitialized) return;
    element.dataset.gaInitialized = 'true';

    const category = element.getAttribute('data-ga-category') || 'swiper';
    const label = element.getAttribute('data-ga-label') || 'slider';

    // Swiper 인스턴스가 초기화될 때까지 대기
    const checkSwiper = setInterval(function () {
      if (element.swiper) {
        clearInterval(checkSwiper);

        element.swiper.on('slideChange', function () {
          sendGAEvent(category, 'slide_change', label, element.swiper.activeIndex + 1);
        });
      }
    }, 100);

    // 최대 5초 대기
    setTimeout(function () {
      clearInterval(checkSwiper);
    }, 5000);
  }

  // 초기화 함수
  function initGATracker() {
    // 모든 GA 트래킹 요소 찾기
    const elements = document.querySelectorAll('[data-ga-event]');

    elements.forEach(function (element) {
      const eventType = element.getAttribute('data-ga-event');

      switch (eventType) {
        case 'click':
          handleClickEvent(element);
          break;
        case 'submit':
          handleSubmitEvent(element);
          break;
        case 'view':
          handleViewEvent(element);
          break;
        case 'play':
        case 'pause':
        case 'ended':
        case 'video':
          handleVideoEvent(element);
          break;
        case 'scroll':
          handleScrollEvent(element);
          break;
        case 'download':
          handleDownloadEvent(element);
          break;
        case 'outbound':
          handleOutboundEvent(element);
          break;
        case 'swiper':
          handleSwiperEvent(element);
          break;
        default:
          console.warn('[GA Tracker] Unknown event type:', eventType);
      }
    });
  }

  // 페이지 로드 시 초기화
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGATracker);
  } else {
    initGATracker();
  }

  // SITE_START 이벤트 발생 시에도 재초기화 (동적 콘텐츠 대응)
  window.addEventListener('SITE_START', initGATracker);

  // 공개 API (필요시 직접 이벤트 전송)
  window.GATracker = {
    send: sendGAEvent,
    init: initGATracker,
  };
})();
